// ============================================================================
// components/remote/RemoteScreen.tsx — просмотр удалённого экрана (viewer):
// attach трека ScreenShare из комнаты remote_{sessionId} (LiveKit).
// onState(ready) — есть ли активный экран (для статуса панели).
// ============================================================================

import { useEffect, useRef } from 'react';
import { Room, RoomEvent, Track, type RemoteTrack } from 'livekit-client';

export function RemoteScreen({ onState }: { onState: (ready: boolean) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const room = (window as unknown as { __hydraRemoteRoom?: Room }).__hydraRemoteRoom;
    if (!room) return;
    let disposed = false;

    const attachAll = () => {
      if (disposed) return;
      const el = videoRef.current;
      if (!el) return;
      let found = false;
      room.remoteParticipants.forEach((p) => {
        p.videoTrackPublications.forEach((pub) => {
          if (pub.source !== Track.Source.ScreenShare || pub.isMuted) return;
          const t = pub.videoTrack;
          if (t) {
            t.attach(el);
            found = true;
          }
        });
      });
      onState(found);
    };

    const onSub = (track: RemoteTrack) => {
      if (track.source === Track.Source.ScreenShare) window.setTimeout(attachAll, 0);
    };
    const onUnsub = (track: RemoteTrack) => {
      if (track.source === Track.Source.ScreenShare) window.setTimeout(attachAll, 0);
    };
    const onParticipant = () => window.setTimeout(attachAll, 300);

    room.on(RoomEvent.TrackSubscribed, onSub);
    room.on(RoomEvent.TrackUnsubscribed, onUnsub);
    room.on(RoomEvent.ParticipantConnected, onParticipant);
    attachAll();

    return () => {
      disposed = true;
      room.off(RoomEvent.TrackSubscribed, onSub);
      room.off(RoomEvent.TrackUnsubscribed, onUnsub);
      room.off(RoomEvent.ParticipantConnected, onParticipant);
      room.remoteParticipants.forEach((p) => {
        p.videoTrackPublications.forEach((pub) => {
          if (pub.source === Track.Source.ScreenShare) {
            try {
              pub.videoTrack?.detach();
            } catch {
              /* элемент уже удалён */
            }
          }
        });
      });
    };
  }, [onState]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="pointer-events-none h-full w-full bg-black object-contain"
    />
  );
}
